import { bibleService } from './bible';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

/** Тот же пул, что api/data/daily-verse-pool.json (fallback на клиенте). */
const HOME_VERSE_POOL = [
  { bookId: 19, chapter: 22, verse: 1 },
  { bookId: 19, chapter: 23, verse: 1 },
  { bookId: 19, chapter: 27, verse: 1 },
  { bookId: 19, chapter: 90, verse: 1 },
  { bookId: 19, chapter: 91, verse: 1 },
  { bookId: 19, chapter: 118, verse: 24 },
  { bookId: 19, chapter: 119, verse: 105 },
  { bookId: 19, chapter: 139, verse: 14 },
  { bookId: 40, chapter: 5, verse: 3 },
  { bookId: 40, chapter: 6, verse: 9 },
  { bookId: 40, chapter: 11, verse: 28 },
  { bookId: 40, chapter: 28, verse: 20 },
  { bookId: 43, chapter: 1, verse: 1 },
  { bookId: 43, chapter: 3, verse: 16 },
  { bookId: 43, chapter: 8, verse: 12 },
  { bookId: 43, chapter: 10, verse: 10 },
  { bookId: 43, chapter: 14, verse: 6 },
  { bookId: 43, chapter: 15, verse: 5 },
  { bookId: 45, chapter: 8, verse: 28 },
  { bookId: 45, chapter: 12, verse: 2 },
  { bookId: 49, chapter: 2, verse: 8 },
  { bookId: 50, chapter: 4, verse: 6 },
  { bookId: 50, chapter: 4, verse: 13 },
  { bookId: 59, chapter: 1, verse: 5 },
  { bookId: 20, chapter: 3, verse: 5 },
  { bookId: 20, chapter: 16, verse: 3 },
  { bookId: 23, chapter: 40, verse: 31 },
  { bookId: 23, chapter: 41, verse: 10 },
  { bookId: 24, chapter: 29, verse: 11 },
  { bookId: 1, chapter: 1, verse: 1 },
  { bookId: 6, chapter: 1, verse: 9 },
  { bookId: 9, chapter: 16, verse: 7 },
  { bookId: 18, chapter: 3, verse: 5 },
  { bookId: 21, chapter: 3, verse: 1 },
  { bookId: 58, chapter: 11, verse: 1 },
  { bookId: 60, chapter: 5, verse: 7 },
  { bookId: 62, chapter: 4, verse: 8 },
  { bookId: 44, chapter: 2, verse: 38 },
  { bookId: 44, chapter: 17, verse: 28 },
  { bookId: 19, chapter: 37, verse: 4 },
  { bookId: 19, chapter: 46, verse: 1 },
  { bookId: 19, chapter: 51, verse: 10 },
  { bookId: 19, chapter: 55, verse: 22 },
  { bookId: 19, chapter: 62, verse: 5 },
  { bookId: 19, chapter: 73, verse: 26 },
  { bookId: 19, chapter: 84, verse: 2 },
  { bookId: 19, chapter: 100, verse: 1 },
  { bookId: 19, chapter: 103, verse: 1 },
  { bookId: 19, chapter: 121, verse: 1 },
  { bookId: 19, chapter: 130, verse: 1 },
  { bookId: 19, chapter: 150, verse: 6 },
];

function shufflePool(pool) {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function normalizeDailyVerse(data, translation) {
  if (!data?.bookId || !data?.chapter) return null;
  return {
    date: data.date || new Date().toISOString().slice(0, 10),
    translation: data.translation || translation,
    bookId: data.bookId,
    chapter: data.chapter,
    verse: data.verse,
    bookName: data.bookName || '',
    text: typeof data.text === 'string' ? data.text.trim() : '',
  };
}

/**
 * Случайный стих с API (refresh=1) или из Библии на клиенте, если API недоступен.
 */
export async function fetchDailyVerse(translation = 'synodal', { refresh = true } = {}) {
  const tr = translation || 'synodal';

  try {
    const params = new URLSearchParams({ translation: tr });
    if (refresh) params.set('refresh', '1');
    const response = await fetch(`${API_BASE}/daily-verse?${params}`, { cache: 'no-store' });
    const data = await response.json().catch(() => null);
    if (response.ok) {
      const normalized = normalizeDailyVerse(
        {
          date: data.date,
          translation: data.translation,
          bookId: data.bookId,
          chapter: data.chapter,
          verse: data.verse,
          bookName: data.bookName || '',
          text: data.text,
        },
        tr
      );
      if (normalized?.text) return normalized;
    }
  } catch {
    /* fallback ниже */
  }

  return fetchRandomVerseClient(tr);
}

/** Имя книги: сначала из списка перевода, иначе с API. */
export function resolveHomeBookName(bookId, books, apiBookName = '') {
  const id = parseInt(bookId, 10);
  if (!Number.isFinite(id)) return '';
  const fromBooks = books?.find((b) => parseInt(b.id, 10) === id)?.name;
  return fromBooks || apiBookName || '';
}

/** Локальный случайный стих (главная всегда показывает текст). */
export async function fetchRandomVerseClient(translation = 'synodal') {
  const tr = translation || 'synodal';
  const books = await bibleService.getBooks(tr);
  const today = new Date().toISOString().slice(0, 10);

  for (const pick of shufflePool(HOME_VERSE_POOL)) {
    try {
      const chapter = await bibleService.getChapter(pick.bookId, pick.chapter, tr);
      const verses = chapter?.verses;
      if (!verses?.length) continue;

      const row =
        verses.find((v) => parseInt(v.verse, 10) === pick.verse) ||
        verses[0];
      const text = String(row?.text || '').trim();
      if (!text) continue;

      const bookName = resolveHomeBookName(
        pick.bookId,
        books,
        chapter?.bookName || ''
      );

      return {
        date: today,
        translation: tr,
        bookId: pick.bookId,
        chapter: pick.chapter,
        verse: row.verse ?? pick.verse,
        bookName,
        text,
      };
    } catch {
      /* try next */
    }
  }

  return null;
}
